"use client"

import { Bars3Icon, HeartIcon, MagnifyingGlassIcon, ShoppingBagIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { Badge } from "~/components/common/badge";
import { Navbar } from "~/components/common/navbar";
import { NavbarSheet } from "~/components/common/navbar-sheet";
import { UserNav } from "~/components/common/user-nav";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "~/components/ui/navigation-menu";
import { useClientAuth } from "~/hooks/use-auth";
import useCart from "~/hooks/use-cart";
import { useTrigger } from "~/hooks/use-trigger";
import { cn, shortName } from "~/lib/utils";
import { menuItems } from "~/routes/header.routes";
import { api } from "~/trpc/react";

const Header = () => {
    const { isAuth, isUser } = useClientAuth();
    const { states: { isOpenMenu, isOpenCart }, toggleState } = useTrigger();

    const { data } = api.wishlists.getWishlist.useQuery(undefined, {
        refetchOnWindowFocus: true,
        enabled: isAuth,
      });
    
      const cartItems = useCart<{ count: number }[]>(
        (state) => (state as { items: { count: number }[] }).items,
      );
      const cartValue = cartItems.reduce((acc, { count }) => acc + count, 0);

    return (
        <Navbar className="sticky top-0 z-50 h-max max-w-full rounded-none p-4 lg:px-8 lg:py-4">
            <div className="mx-auto flex max-w-7xl items-center justify-between lg:px-0 xl:px-8">
                <div className="flex items-center gap-2">
                    <NavbarSheet placement="left" title="Menu" store isOpen={isOpenMenu ?? false} onClose={() => toggleState('isOpenMenu')}>
                        <Button variant="ghost" className="[&_svg]:size-6 p-2 block lg:hidden" onClick={() => toggleState('isOpenMenu')}>
                            <Bars3Icon strokeWidth={2} />
                        </Button>
                    </NavbarSheet>
                    <Link href="/" className="mr-4 block cursor-pointer truncate py-1.5 font-sans text-base font-semibold leading-relaxed tracking-normal text-inherit antialiased">
                        Dev Mobile
                    </Link>
                </div>
                <div className="hidden lg:block">
                    <NavList />
                </div>
                <div className="gap-5 lg:flex">
                    <div className="flex gap-3">
                        <Button className="[&_svg]:size-5 size-[40px]" variant="secondary">
                            <MagnifyingGlassIcon strokeWidth={2} />
                        </Button>
                        <Link href="/wishlists">
                            <Badge withBorder placement="top-end" withContent content={`${data?.count ?? 0}`} className="z-10">
                                <Button className="[&_svg]:size-5 size-[40px]" variant="secondary">
                                    <HeartIcon strokeWidth={2} />
                                </Button>
                            </Badge>
                        </Link>
                        <NavbarSheet placement="right" title="Shopping Cart" cart isOpen={isOpenCart ?? false} onClose={() => toggleState('isOpenCart')}>
                            <Badge withBorder placement="top-end" withContent content={`${cartValue ?? 0}`}>
                                <Button className="[&_svg]:size-5 size-[40px]" variant="secondary" onClick={() => toggleState('isOpenCart')}>
                                    <ShoppingBagIcon strokeWidth={2} />
                                </Button>
                            </Badge>
                        </NavbarSheet>
                    </div>
                    {!!isAuth ? (
                        <div className="hidden lg:block">
                            <UserNav>
                                <Button variant="ghost" className="relative rounded-full size-[40px]">
                                    <Avatar>
                                        <AvatarFallback>{shortName(isUser?.name ?? "RK")}</AvatarFallback>
                                    </Avatar>
                                </Button>
                            </UserNav>
                        </div>
                    ) : (
                        <Link href="/sign-in">
                            <Button className="hidden lg:block rounded-lg h-auto text-xs py-3 px-6 opacity-95">Sign In</Button>
                        </Link>
                    )}
                </div>
            </div>
        </Navbar>
    )
}

export { Header };

function NavList() {
    return (
        <NavigationMenu delayDuration={0}>
            <NavigationMenuList>
                {menuItems.map((item, index) => (
                    <NavigationMenuItem key={index}>
                        {item.subMenu ? (
                            <>
                                <NavigationMenuTrigger className="h-auto rounded-lg bg-transparent cursor-pointer">{item.title}</NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul className="grid gap-y-2 p-4 grid-cols-3">
                                        {item.subMenuItems.map((item2, i) => (
                                            <ListItem className="flex gap-3" key={i} image={item2.image} title={item2.title} href={`/${item.link}/${item2.subLink}`}>{item2.description}</ListItem>
                                        ))}
                                    </ul>
                                </NavigationMenuContent>
                            </>
                        ) : (
                                <NavigationMenuLink asChild>
                                    <Link href={item.link} passHref className={cn(navigationMenuTriggerStyle(), "h-auto rounded-lg bg-transparent")}>
                                        {item.title}
                                    </Link>
                                </NavigationMenuLink>
                        )}
                    </NavigationMenuItem>
                ))}
            </NavigationMenuList>
        </NavigationMenu>
    )
}

interface ListItemProps extends React.ComponentPropsWithoutRef<"a"> {
    image: string;
    title: string;
}

const ListItem = React.forwardRef<
    React.ComponentRef<"a">,
    ListItemProps
>(({ className, title, href, children, image, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          href={href ?? ""}
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md py-2 px-3 leading-none no-underline outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground items-center",
            className
          )}
          {...props}
        >
            <Image src={`/menu/${image}`} alt={title} loading="lazy" width="50" height="50" className="rounded-lg size-[50px]" priority={false} />
            <div>
                <h6 className="text-sm font-bold leading-none">{title}</h6>
                <p className="text-xs leading-snug font-medium text-muted-foreground mt-1">
                    {children}
                </p>
            </div>
        </Link>
      </NavigationMenuLink>
    </li>
  )
})
ListItem.displayName = "ListItem"